//
// Copyright (c) 2008-2025 Barry E. Mapen All Rights Reserved
//
import { useContext } from 'react';
import classNames from 'classnames';

import { useList } from './useList';
import { FormContext } from './Form';

import * as patch from './PatchFunctions';


export function LookupSelect({
    name,
    id,
    listName = name,        // Assume the name field is the listName unless specified otherwise
    value,
    className = "",
    getList,                // Local getList will override the form getList parameter
    disabled = false,
    clearOnDisable = false, 
    readOnly = false,
    group,
    groupUsing,
    removeBlanks = false,
    bypassCache = false,
    displayValue = false,
    onChange,
    fallback,
    children,
    ...rest
}) {
    
    const { 
        initial, current, formId, formDisabled, formReadOnly, defaultOnChange, formGetList
    } = useContext(FormContext);

    //
    // Get the list and information about it's status which might still be 
    //  loading or that it failed to load. We use the local getList if available
    //  and fall back on the formGetList if it's not present
    //
    var { list, loading, error } = useList(getList ?? formGetList, listName, bypassCache);


    // Extract the initial value for this named item in the form fields
    const initValue = patch.getPath(initial, name);

    // Use any parameters pass into this field
    const currId = id ? id : `${name}-${formId}`;
    const inputValue = value ? value : patch.getPath(current, name);
    const currDisabled = disabled || formDisabled;
    const currReadOnly = readOnly || formReadOnly;
    const currValue = (currDisabled && clearOnDisable) ? "0" : inputValue; 

    const selectClass = classNames(
        'form-select',
        className,
        { 'is-dirty': currValue !== initValue }
    );


    //
    // During loading or errors, show the user a more useful control
    //
    if (loading || error) {
        let text;

        if (loading && !error) {
            text = `loading...`
        }
        else if (error) {
            text = `error loading ${listName} `
        }

        return(
            <select
                className={selectClass}
                id={formId}
                disabled={currDisabled}
                readOnly={true}
                defaultValue={ rest.multiple ? ["0"] : "0" }
                {...rest}
            >
                <option value="0">
                    {text}
                </option>
            </select>
        );
    }

    //
    // If we're grouping using another form field, lookup it's value only if there
    //  isn't some overriding group specifier also provided
    //
    if (!group && groupUsing) {
        group = patch.getPath(current, groupUsing);
    }

    //
    // If grouping is enabled, trim the list to only the active group
    //  but keep any blanks
    //
    if (group) {
        list = list.filter((x) => ( (x.group === group) ));
    }

    //
    // If removeBlanks is requested eliminate anything with an empty text string
    //
    if (removeBlanks) {
        list = list.filter((x) => x.text.trim() !== "")
    }

    //
    // Remove hidden items from the list
    //
    list = list.filter((x) => x.hidden !== true);

    //
    // Create an array of remaining options to populate within the select
    //
    const listItems = list.map((x) => 
        <option key={`${x.group}-${x.value}`} value={x.value} disabled={x.disabled}>
            {displayValue ? x.value : x.text}
        </option>
    );



    return(
        <select 
            className={selectClass}
            name={name}
            id={currId}
            value={currValue ? currValue : ""}
            onChange={(e) => defaultOnChange(e, onChange)}
            disabled={currDisabled}
            readOnly={currReadOnly}
            {...rest}
        >
            {listItems}
        </select>
    );
}
